import { environment } from "../../config/environment";
import { storage } from "../../utils/storage";
import { ApiErrorResponse } from "../../types/api";
import { ROUTES } from "../../constants/routes";

export class AppError extends Error {
  code: number;
  errorCode: number;
  details: string;

  constructor(message: string, code: number, errorCode: number, details: string = '') {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.errorCode = errorCode;
    this.details = details;
  }
}

export class AppPreConsultError extends AppError {
  isPreConsultationError: true = true;
  response: { status: number; data: unknown };

  constructor(message: string, code: number, errorCode: number, details: string, status: number, data: unknown) {
    super(message, code, errorCode, details);
    this.name = 'AppPreConsultError';
    this.response = { status, data };
  }
}

function normalizeErrorData(rawError: Record<string, unknown>, status: number) {
  if (rawError.error_code !== undefined) {
    return {
      error_code: typeof rawError.error_code === 'string' ? parseInt(rawError.error_code) : rawError.error_code as number,
      message: (rawError.message as string) || `HTTP error! status: ${status}`,
      details: (rawError.details as string) || ''
    };
  } else if (rawError.error) {
    return {
      error_code: status,
      message: rawError.error as string,
      details: JSON.stringify(rawError)
    };
  }
  return {
    error_code: status,
    message: (rawError.message as string) || `HTTP error! status: ${status}`,
    details: JSON.stringify(rawError)
  };
}

function throwApiError(errorData: { error_code: number; message: string; details: string }, status: number, responseData: unknown): never {
  if (errorData.error_code === 2101 || errorData.error_code === 2400) {
    throw new AppPreConsultError(
      errorData.message || 'Pre-consultation not completed',
      errorData.error_code,
      errorData.error_code,
      errorData.details,
      status,
      responseData
    );
  }
  throw new AppError(errorData.message, status, errorData.error_code, errorData.details);
}

export class BaseApiService {
  protected baseUrl: string;
  protected authUrl: string;
  protected authSecUrl: string;
  private isRefreshing = false;
  private refreshQueue: Array<{ resolve: (value: boolean) => void }> = [];

  constructor() {
    this.baseUrl = environment.API_URL;
    this.authUrl = environment.AUTH_API_URL;
    this.authSecUrl = environment.AUTH_SEC_API_URL;
  }

  protected async fetchWithAuth<T = unknown>(endpoint: string, options: RequestInit = {}, isRetry = false): Promise<T> {
    // Clone options to avoid mutating the caller's object on retry
    const opts = { ...options, headers: { ...options.headers } };

    try {
      const accessToken = storage.getAccessToken();

      if (accessToken) {
        opts.headers = {
          ...opts.headers,
          'Authorization': `Bearer ${accessToken}`
        };
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, opts);

      // Handle 401 Unauthorized — only attempt refresh+retry once to prevent infinite loops
      if (response.status === 401 && !isRetry) {
        const refreshed = await this.handleTokenRefresh();
        if (refreshed) {
          // Retry the original request with new token (fresh opts will get new token)
          return this.fetchWithAuth(endpoint, options, true);
        }
      }

      if (!response.ok) {
        let errorData: ApiErrorResponse;
        try {
          const rawError = await response.json();
          errorData = normalizeErrorData(rawError, response.status);
        } catch {
          errorData = {
            error_code: response.status,
            message: response.statusText || `HTTP error! status: ${response.status}`,
            details: ''
          };
        }
        throwApiError(errorData, response.status, errorData);
      }

      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');

      if (response.status === 204 || contentLength === '0' || !contentType?.includes('application/json')) {
        return { status: 'success', message: 'Operation completed successfully' } as T;
      }

      return await response.json() as T;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  protected async fetchWithAuthUrl<T = unknown>(endpoint: string, options: RequestInit = {}, urlType: 'auth' | 'auth-sec' = 'auth-sec', isRetry = false): Promise<T> {
    // Clone options to avoid mutating the caller's object on retry
    const opts = { ...options, headers: { ...options.headers } };

    try {
      const accessToken = storage.getAccessToken();

      if (accessToken) {
        opts.headers = {
          ...opts.headers,
          'Authorization': `Bearer ${accessToken}`
        };
      }

      const baseAuthUrl = urlType === 'auth' ? this.authUrl : this.authSecUrl;
      const response = await fetch(`${baseAuthUrl}${endpoint}`, opts);

      // Handle 401 Unauthorized — only attempt refresh+retry once to prevent infinite loops
      if (response.status === 401 && !isRetry) {
        const refreshed = await this.handleTokenRefresh();
        if (refreshed) {
          return this.fetchWithAuthUrl(endpoint, options, urlType, true);
        }
      }

      if (!response.ok) {
        let errorData: ApiErrorResponse;
        try {
          const rawError = await response.json();
          errorData = normalizeErrorData(rawError, response.status);
        } catch {
          errorData = {
            error_code: response.status,
            message: response.statusText || `HTTP error! status: ${response.status}`,
            details: ''
          };
        }
        throwApiError(errorData, response.status, errorData);
      }

      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');

      if (response.status === 204 || contentLength === '0' || !contentType?.includes('application/json')) {
        return { status: 'success', message: 'Operation completed successfully' } as T;
      }

      return await response.json() as T;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  private async handleTokenRefresh(): Promise<boolean> {
    if (this.isRefreshing) {
      return new Promise<boolean>((resolve) => {
        this.refreshQueue.push({ resolve });
      });
    }

    this.isRefreshing = true;

    try {
      const refreshToken = storage.getRefreshToken();

      if (!refreshToken) {
        storage.clearAuthData();
        this.handleAuthFailure();
        this.drainQueue(false);
        return false;
      }

      const requestBody = {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: environment.CLIENT_ID,
      };

      const response = await fetch(`${environment.LOGIN_API_URL}/refreshtoken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        storage.clearAuthData();
        this.handleAuthFailure();
        this.drainQueue(false);
        return false;
      }

      const data = await response.json();
      const existingData = storage.getAuthData();

      storage.setAuthData({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in,
        user: existingData?.user || {
          name: '',
          email: '',
          phone_no: '',
          age: '',
          gender: '',
          country: '',
          specialty: '',
          clinic_id: 0,
          is_admin: false,
          is_super_admin: false,
          is_patient: false,
          is_medical_officer: false
        },
        registration_status: existingData?.registration_status
      });

      this.drainQueue(true);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      storage.clearAuthData();
      this.handleAuthFailure();
      this.drainQueue(false);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  private drainQueue(success: boolean): void {
    this.refreshQueue.forEach(({ resolve }) => resolve(success));
    this.refreshQueue = [];
  }

  private handleAuthFailure(): void {
    if (typeof window !== 'undefined') {
      window.location.href = ROUTES.LOGIN;
    }
  }

}